<template>
  <div>
    <base-card header-color="warning" :has-footer="true">
      <template slot="header">
        <div class="card-icon">
          <md-icon>pie_chart</md-icon>
        </div>
        <h4 class="title">{{ $t('dashboard.seo.card.title') }}</h4>
      </template>

      <template slot="content">
        <div class="md-layout">
          <div class="md-layout-item content">
            <client-only>
              <vue-highcharts
                ref="chart"
                :options="drilldownOptions"
                :highcharts="Highcharts"
              ></vue-highcharts>
            </client-only>
          </div>
        </div>
      </template>
      <template slot="footer">
        <span v-html="$t('dashboard.seo.card.description')"></span>
      </template>
    </base-card>
  </div>
</template>

<script>
import Vuex from 'vuex'
// https://www.npmjs.com/package/vue2-highcharts
// https://github.com/superman66/vue-highcharts#using-with-nuxtjs
// https://api.highcharts.com/highcharts/
// https://www.highcharts.com/demo
// https://www.highcharts.com/demo/polar-radial-bar
// https://codesandbox.io/s/jjyqvv0k13 => demo vuejs with different charts types
// import Highcharts from 'highcharts'
// import HighchartsMore from 'highcharts/highcharts-more'
import Highcharts from 'highcharts'
// import Drilldown from 'highcharts/modules/drilldown.js'
import BaseCard from '@/components/admin/common/BaseCard'
import { tools } from '@/plugins/tools.js'

// HighchartsMore(Highcharts)

export default {
  name: 'SeoCard',
  components: {
    BaseCard
  },
  props: {},
  data() {
    return {
      tools,
      Highcharts,
      fetchedSeoWs: 0,
      hasPlace: false,
      categories: [],
      serie1: [],
      serie2: [],
      serie3: [],
      drilldownOptions: {
        colors: ['#f44336', '#ff9800', '#4caf50'],
        chart: {
          type: 'column'
          //          inverted: true (to put horizontal)
          //          polar: true
        },
        title: {
          text: this.$t('dashboard.seo.card.sub_title')
        },
        tooltip: {
          outside: false
        },
        pane: {
          size: '85%',
          endAngle: 270
        },
        xAxis: {
          tickInterval: 1,
          labels: {
            align: 'center',
            useHTML: true,
            allowOverlap: false,
            step: 1,
            y: 4,
            style: {
              fontSize: '12px'
            }
          },
          lineWidth: 0,
          categories: []
        },
        yAxis: {
          min: 0,
          max: 100,
          lineWidth: 0,
          tickInterval: 25,
          reversedStacks: false,
          endOnTick: true,
          showLastLabel: true
        },
        plotOptions: {
          column: {
            stacking: 'normal',
            borderWidth: 0,
            pointPadding: 0,
            groupPadding: 0.15
          }
        },
        series: [
          {
            name: this.$t('dashboard.seo.card.bad'),
            data: []
          },
          {
            name: this.$t('dashboard.seo.card.medium'),
            data: []
          },
          {
            name: this.$t('dashboard.seo.card.good'),
            data: []
          }
        ]
      }
    }
  },
  computed: {
    ...Vuex.mapGetters(['loggedInUser']),
    ...Vuex.mapGetters('center', ['getCenterToken'])
  },
  watch: {
    getCenterToken(val) {
      this.fetchSeoData()
    },
    fetchedSeoWs(val) {
      if (val === 2) {
        if (!this.hasPlace) {
          // No place file
          this.addCategory(this.$t('dashboard.seo.card.no_place'))
          this.pushSeries(1)
        }
      }
    }
  },
  created() {},
  mounted() {
    this.$nextTick(() => {
      if (this.getCenterToken) {
        this.fetchSeoData()
      }
    })
  },
  methods: {
    ...Vuex.mapActions('place', ['fetchMyPlaces', 'fetchFavoritePlaces']),
    fetchSeoData() {
      this.initSeoChartData()
      this.fetchMyPlaces(this.getCenterToken).then((myPlaces) => {
        if (myPlaces && myPlaces.length) {
          myPlaces.forEach((myPlace) => {
            this.addCategory(
              this.tools.getTranslation(
                myPlace.place,
                'name',
                this.$i18n.locale
              )
            )
            this.pushSeries(this.tools.placePercent(myPlace.place))
          })
        }
        this.fetchedSeoWs += 1
        this.hasPlace = true
      })
      this.fetchFavoritePlaces(this.getCenterToken).then((favoritePlaces) => {
        if (favoritePlaces.data && favoritePlaces.data.length) {
          favoritePlaces.data.forEach((favoritePlace) => {
            this.addCategory(
              this.tools.getTranslation(
                favoritePlace.place,
                'name',
                this.$i18n.locale
              )
            )
            this.pushSeries(this.tools.placePercent(favoritePlace.place))
          })
        }
        this.fetchedSeoWs += 1
        this.hasPlace = true
      })
    },
    initSeoChartData() {
      this.categories = this.$refs.chart.chart.xAxis[0]
      this.serie1 = this.$refs.chart.chart.series[0]
      this.serie2 = this.$refs.chart.chart.series[1]
      this.serie3 = this.$refs.chart.chart.series[2]

      // Business contact
      this.addCategory(this.$t('teacher.business_contact'))
      const percent = this.tools.getTeacherProfilePercent(this.loggedInUser)
      this.pushSeries(percent)

      // Disciplines files
      if (
        this.loggedInUser &&
        this.loggedInUser.teacher &&
        this.loggedInUser.teacher.disciplines &&
        this.loggedInUser.teacher.disciplines.length > 0
      ) {
        this.loggedInUser.teacher.disciplines.forEach((discipline) => {
          this.addCategory(
            this.$t('teacher.discipline_card', {
              discipline: this.tools.getTranslation(
                discipline.discipline,
                'name',
                this.$i18n.locale
              )
            })
          )
          const fieldsList = [
            'description',
            'inspiration',
            'citation',
            'reference'
          ]
          this.pushSeries(
            this.tools.teacherDisciplinePercent(discipline, fieldsList)
          )
        })
      } else {
        // No discipline file
        this.addCategory(this.$t('teacher.discipline_card'))
        this.pushSeries(1)
      }
    },
    addCategory(category) {
      const cat = this.categories.categories
      cat.push(category)
      this.categories.setCategories(cat, true)
    },
    pushSeries(percent) {
      if (percent === 0) {
        percent = 1
      }
      if (percent <= 50) {
        this.serie1.addPoint(percent)
        this.serie2.addPoint(0)
        this.serie3.addPoint(0)
      } else if (percent <= 85) {
        this.serie1.addPoint(0)
        this.serie2.addPoint(percent)
        this.serie3.addPoint(0)
      } else {
        this.serie1.addPoint(0)
        this.serie2.addPoint(0)
        this.serie3.addPoint(percent)
      }
    }
  }
}
</script>

<style lang="scss" scoped></style>
